import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private static readonly CHECK_INTERVAL_MS = 12 * 60 * 60 * 1000;

  private readonly runningScripts = this.getScriptPaths(document);
  private checkInterval?: ReturnType<typeof setInterval>;

  constructor(private authService: AuthService) { }

  start(): void {
    if (this.checkInterval || !this.shouldCheckForUpdates() || !this.authService.hasLongRunningSession()) {
      return;
    }

    this.checkInterval = setInterval(
      () => void this.checkForUpdate(),
      AppUpdateService.CHECK_INTERVAL_MS
    );
  }

  stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
  }

  private async checkForUpdate(): Promise<void> {
    try {
      const response = await fetch(`/index.html?update-check=${Date.now()}`, {
        cache: 'no-store'
      });

      if (!response.ok) {
        return;
      }

      const latestDocument = new DOMParser().parseFromString(await response.text(), 'text/html');

      if (this.hasNewVersion(latestDocument)) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('app-update', Date.now().toString());
        window.location.replace(currentUrl.toString());
      }
    } catch {
      // A temporary network failure should not interrupt a monitoring screen.
    }
  }

  private shouldCheckForUpdates(): boolean {
    return ['http:', 'https:'].includes(window.location.protocol)
      && window.location.hostname !== 'localhost'
      && this.runningScripts.length > 0;
  }

  private hasNewVersion(latestDocument: Document): boolean {
    const latestScripts = this.getScriptPaths(latestDocument);

    return latestScripts.length > 0
      && latestScripts.join('|') !== this.runningScripts.join('|');
  }

  private getScriptPaths(sourceDocument: Document): string[] {
    return Array.from(sourceDocument.querySelectorAll<HTMLScriptElement>('script[src]'))
      .map(script => new URL(script.src, window.location.origin).pathname)
      .sort();
  }
}